import { useRef, useState } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import { ErrorMessage, useMessageService } from 'framework/ui/Message';

interface TimelinePinActionProps {
  isPinned: boolean;
  disabled?: boolean;
  onChange: (isPinned: boolean) => Promise<void>;
}

const TimelinePinAction = ({ isPinned, disabled, onChange }: TimelinePinActionProps) => {
  const { addMessage } = useMessageService();
  const [isSaving, setIsSaving] = useState(false);
  const savingRef = useRef(false);
  const label = isPinned ? 'Unpin' : 'Pin';

  const handleClick = async () => {
    if (disabled || savingRef.current) return;
    savingRef.current = true;
    setIsSaving(true);
    try {
      await onChange(!isPinned);
    } catch {
      addMessage(new ErrorMessage(`Could not ${isPinned ? 'unpin' : 'pin'} this record. Please try again.`));
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  };

  return (
    <Tooltip title={label}>
      <span>
        <IconButton
          aria-label={label}
          aria-pressed={isPinned}
          disabled={disabled || isSaving}
          onClick={handleClick}
          size="small"
        >
          {isPinned ? (
            <PushPinIcon sx={{ fontSize: 16 }} color="primary" />
          ) : (
            <PushPinOutlinedIcon sx={{ fontSize: 16, transform: 'rotate(45deg)' }} />
          )}
        </IconButton>
      </span>
    </Tooltip>
  );
};

export default TimelinePinAction;
